import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './lib/auth';

// ── Lazy page imports (add as you build them) ─────────────────────────────────
import LoginPage           from './pages/Login';
import RegisterPage        from './pages/Register';
import ForgotPassword      from './pages/ForgotPassword';
import ResetPassword       from './pages/ResetPassword';
import VerifyEmailPending  from './pages/VerifyEmailPending';
import VerifyEmail         from './pages/VerifyEmail';
import MagicLogin          from './pages/MagicLogin';
import AcceptInvite        from './pages/AcceptInvite';
import DashboardPage    from './pages/Dashboard';
import RegistrationsPage from './pages/Registrations';
import RegisterProduct  from './pages/RegisterProduct';
import ClaimsPage       from './pages/Claims';
import ClaimDetail   from './pages/ClaimDetail';
import NewClaim      from './pages/NewClaim';
import AdminDashboard   from './pages/admin/Dashboard';
import AdminClaims         from './pages/admin/Claims';
import AdminRegistrations  from './pages/admin/AdminRegistrations';
import AdminCompanies      from './pages/admin/Companies';
import AdminUsers          from './pages/admin/Users';
import AdminProducts       from './pages/admin/Products';
import AdminDistributors  from './pages/admin/Distributors';
import Support          from './pages/Support';
import Settings         from './pages/Settings';
import Five             from './pages/Five';

function RequireAuth({ children }: { children: JSX.Element }) {
  const { user } = useAuth();
  return user ? children : <Navigate to="/login" replace />;
}

function RequireAdmin({ children }: { children: JSX.Element }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin') return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public */}
        <Route path="/login"           element={<LoginPage />} />
        <Route path="/register"        element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password"  element={<ResetPassword />} />
        <Route path="/verify-pending"  element={<VerifyEmailPending />} />
        <Route path="/verify-email"    element={<VerifyEmail />} />
        <Route path="/magic-login"     element={<MagicLogin />} />
        <Route path="/accept-invite"   element={<AcceptInvite />} />

        {/* Customer portal */}
        <Route path="/" element={<RequireAuth><DashboardPage /></RequireAuth>} />
        <Route path="/registrations" element={<RequireAuth><RegistrationsPage /></RequireAuth>} />
        <Route path="/registrations/new" element={<RequireAuth><RegisterProduct /></RequireAuth>} />
        <Route path="/claims/new" element={<RequireAuth><NewClaim /></RequireAuth>} />
        <Route path="/claims" element={<RequireAuth><ClaimsPage /></RequireAuth>} />
        <Route path="/claims/:id" element={<RequireAuth><ClaimDetail /></RequireAuth>} />

        <Route path="/five"     element={<RequireAuth><Five /></RequireAuth>} />
        <Route path="/support"  element={<RequireAuth><Support /></RequireAuth>} />
        <Route path="/settings" element={<RequireAuth><Settings /></RequireAuth>} />
        {/* Legacy redirects */}
        <Route path="/account"       element={<Navigate to="/settings" replace />} />
        <Route path="/company"       element={<Navigate to="/settings?tab=company" replace />} />
        <Route path="/company/setup" element={<Navigate to="/settings?tab=company" replace />} />

        {/* Admin */}
        <Route path="/admin" element={<RequireAdmin><AdminDashboard /></RequireAdmin>} />
        <Route path="/admin/claims"         element={<RequireAdmin><AdminClaims /></RequireAdmin>} />
        <Route path="/admin/registrations"  element={<RequireAdmin><AdminRegistrations /></RequireAdmin>} />
        <Route path="/admin/companies"      element={<RequireAdmin><AdminCompanies /></RequireAdmin>} />
        <Route path="/admin/users"          element={<RequireAdmin><AdminUsers /></RequireAdmin>} />
        <Route path="/admin/products"       element={<RequireAdmin><AdminProducts /></RequireAdmin>} />
        <Route path="/admin/distributors"   element={<RequireAdmin><AdminDistributors /></RequireAdmin>} />
      </Routes>
    </BrowserRouter>
  );
}
